import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Button, Popover } from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import NavigationBlocker from '@src/new-components/NavigationBlocker';
import FileDropzoneV3 from '@src/components/FileDropzoneV3';
import DropdownCustom from '@src/new-components/Dropdown';
import Typography from '@src/new-components/Typography';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useCreditsLocked from '@src/hooks/useCreditsLocked';
import useTotalCredits from '@src/hooks/useTotalCredits';
import actions from '@src/redux/actions';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import CrownImg from '@src/assets/images/crown.png';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { LANGUAGE } from '@src/constants';
import { COLOR } from '@src/styles/color';
import { FEATURE } from '@src/constants/package';
import { ERROR_CODE } from '@src/errors/code';
import { PROJECT_PRODUCT, PROJECT_TYPE } from '@src/constants/project';
import { MOE_SURFACE } from '@src/constants/moengage';
import { trackProjectCreated } from '@src/services/moengage';
import route from '@src/constants/route';
import PopupUpPkgUseRewrite from '@src/containers/ProjectDetail/Dubbing/Content/PopupUpPkgUseRewrite';
import {
  TRANSCRIPT_FILE_FORMAT,
  TRANSCRIPT_S3_DIRECTORY,
  MAX_AUDIO_SIZE_BYTES,
  MAX_AUDIO_DURATION_SECONDS,
} from '@src/constants/transcript';
import { uploadToS3FromFile } from '@src/services/upload';
import { formatFileSize } from '@src/utils/file';
import { getAudioDuration, formatDuration } from '@src/services/audio';
import { calculateTranscriptCredit } from '@src/services/transcript';
import { getMinPackageNameForFeature } from '@src/services/package';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import {
  StyledExtraContent,
  StyledRow,
  StyledModelAnchor,
  StyledPopoverList,
  StyledPopoverItem,
  StyledPopoverItemText,
  StyledItalicTypography,
  StyledTipBox,
  StyledTipText,
} from './index.style';

const UploadFileByAudio = ({ open, onClose, handleCreateProject }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isLeavingRef = useRef(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { languagesV3 } = useSelector((state) => state.language);
  const { language: currentLang } = i18n;
  const { getFeatureValue } = useFeatureFlags();
  const { isCreditsLocked } = useCreditsLocked();
  const { totalCredits } = useTotalCredits();
  const [loadingGetContentFile, setLoadingGetContentFile] = useState(false);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(false);
  const [openDialogNotEnoughCredits, setOpenDialogNotEnoughCredits] =
    useState(false);
  const [openPopupUpPkg, setOpenPopupUpPkg] = useState(false);
  const [contentLanguage, setContentLanguage] = useState('vi');
  const [selectedModel, setSelectedModel] = useState('');
  const [modelAnchorEl, setModelAnchorEl] = useState(null);
  const [duration, setDuration] = useState(0);

  const { packages } = useMappingPackage();
  const userFeatures = user?.studioUsageOptions?.features || [];
  const hasTranscriptPermission = userFeatures.includes(
    FEATURE.TRANSCRIPT_DUBBING,
  );

  const transcriptAIModelsConfig = getFeatureValue(
    FEATURE_KEYS.TRANSCRIPT_AUDIO_MODELS,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const transcriptAIModels = useMemo(() => {
    if (!Array.isArray(transcriptAIModelsConfig)) return [];
    return transcriptAIModelsConfig.filter(
      (model) =>
        model?.model && (model.description?.en || model.description?.vi),
    );
  }, [transcriptAIModelsConfig]);

  const getModelDescription = useCallback(
    (model) => {
      const description = model?.description || {};
      if (currentLang === LANGUAGE.EN) {
        return description.en || description.vi || '';
      }
      return description.vi || description.en || '';
    },
    [currentLang],
  );

  useEffect(() => {
    if (open) {
      isLeavingRef.current = false;
      if (!languagesV3?.length) {
        dispatch(actions.language.getLanguagesV3());
      }
      return;
    }
    setLoadingGetContentFile(false);
    setOpenDialogLockedCycle(false);
    setOpenDialogNotEnoughCredits(false);
    setOpenPopupUpPkg(false);
    setContentLanguage('vi');
    setSelectedModel('');
    setModelAnchorEl(null);
    setDuration(0);
  }, [open]);

  useEffect(() => {
    if (!transcriptAIModels.length) {
      setSelectedModel('');
      return;
    }
    const hasSelected = transcriptAIModels.some(
      (m) => m.model === selectedModel,
    );
    if (!hasSelected) {
      setSelectedModel(transcriptAIModels[0].model);
    }
  }, [transcriptAIModels, selectedModel]);

  const selectedModelData = useMemo(
    () => transcriptAIModels.find((m) => m.model === selectedModel),
    [transcriptAIModels, selectedModel],
  );

  const languageOptions = useMemo(() => {
    const autoOption = { label: t('autoDetect'), value: 'auto' };
    const supported = selectedModelData?.supportedLanguage;

    // supportedLanguage = [] → model only supports auto-detect
    if (Array.isArray(supported) && supported.length === 0) {
      return [autoOption];
    }

    const filtered = [...(languagesV3 || [])]
      .filter((item) => item.value !== 'multilingual')
      .filter(
        (item) => !Array.isArray(supported) || supported.includes(item.value),
      )
      .sort((a, b) =>
        a.rank !== b.rank
          ? a.rank - b.rank
          : a.label?.[currentLang]?.localeCompare(b.label?.[currentLang]),
      )
      .map((item) => ({
        ...item,
        label: item.label?.[currentLang] || item.label?.[LANGUAGE.EN],
      }));

    return [autoOption, ...filtered];
  }, [languagesV3, currentLang, selectedModelData, t]);

  // If the currently-selected language is no longer valid for the active model
  // (e.g. user switched to a model that doesn't support it), fall back to auto.
  useEffect(() => {
    const isValid = languageOptions.some((o) => o.value === contentLanguage);
    if (!isValid) setContentLanguage('auto');
  }, [languageOptions, contentLanguage]);

  const getSelectedLabel = (value) =>
    languageOptions.find((item) => item.value === value)?.label || '';

  const handleFileChange = useCallback(
    async (file) => {
      if (!file) {
        setDuration(0);
        return;
      }
      const seconds = await getAudioDuration(file);
      if (!seconds) {
        throw new Error(t('invalidNoAudio'));
      }
      if (seconds > MAX_AUDIO_DURATION_SECONDS) {
        setDuration(0);
        throw new Error(
          t('audioDurationExceeded', {
            max: formatDuration(MAX_AUDIO_DURATION_SECONDS),
          }),
        );
      }
      setDuration(seconds);
    },
    [dispatch, t],
  );
  const creditCost = calculateTranscriptCredit({
    durationSeconds: duration,
    model: selectedModelData,
  });

  // Model name from feature flag maps 1:1 to a feature in the user's package
  // (e.g. model "vbee-transcribe-flash" → package feature "vbee-transcribe-flash").
  const hasSelectedModelPermission =
    !selectedModel || userFeatures.includes(selectedModel);

  const missingFeature = !hasTranscriptPermission
    ? FEATURE.TRANSCRIPT_DUBBING
    : selectedModel;
  const upgradePkgName = useMemo(
    () =>
      getMinPackageNameForFeature({
        packages,
        feature: missingFeature,
        language: currentLang,
      }),
    [packages, missingFeature, currentLang],
  );
  const handleAddFile = async (file) => {
    if (!hasTranscriptPermission || !hasSelectedModelPermission) {
      setOpenPopupUpPkg(true);
      return;
    }
    if (isCreditsLocked) {
      setOpenDialogLockedCycle(true);
      return;
    }
    if (creditCost > 0 && totalCredits < creditCost) {
      setOpenDialogNotEnoughCredits(true);
      return;
    }
    setLoadingGetContentFile(true);
    try {
      const fileUrl = await uploadToS3FromFile(file, TRANSCRIPT_S3_DIRECTORY);
      if (!fileUrl) {
        throw new Error('upload-failed');
      }
      const title = t('untitledTitleProject', {
        time: moment().format('YYYYMMDD-HH:mm'),
      });
      const fileType = file.type.startsWith('video/')
        ? PROJECT_TYPE.VIDEO
        : PROJECT_TYPE.AUDIO;
      // Omit language when user picks auto-detect — backend infers from audio
      const mappedLanguage =
        contentLanguage === 'auto'
          ? undefined
          : languagesV3.find((o) => o.value === contentLanguage)?.value ||
            contentLanguage;
      const res = await handleCreateProject({
        title,
        product: PROJECT_PRODUCT.DUBBING,
        isDeleted: false,
        blocks: [],
        source: {
          url: fileUrl,
          type: fileType,
          ...(mappedLanguage ? { language: mappedLanguage } : {}),
          model: selectedModel || undefined,
          metadata: {
            duration,
            size: file.size,
          },
        },
      });
      if (!res?.status) {
        if (res?.errorCode === ERROR_CODE.FEATURE_NOT_IN_PACKAGE) {
          setOpenPopupUpPkg(true);
          return;
        }
        throw new Error('create-failed');
      }
      const projectId = res?.result?.project?.id;
      trackProjectCreated({
        product: PROJECT_PRODUCT.DUBBING,
        source: { type: fileType },
      });
      if (!isLeavingRef.current) {
        dispatch(actions.project.setProduct(PROJECT_PRODUCT.DUBBING));
        onClose();
        history.push(`${route.PROJECTS}/${projectId}`);
      }
    } catch (err) {
      const backendErrorCode =
        err?.response?.data?.error_code ?? err?.response?.data?.errorCode;
      if (backendErrorCode === ERROR_CODE.FEATURE_NOT_IN_PACKAGE) {
        setOpenPopupUpPkg(true);
        return;
      }
      const messageKey =
        err.message === 'create-failed' ? 'createProjectFailed' : 'uploadError';
      dispatch(actions.noti.push({ severity: 'error', message: messageKey }));
    } finally {
      setLoadingGetContentFile(false);
    }
  };

  const extraContent = (
    <StyledExtraContent>
      {transcriptAIModels.length > 0 && (
        <StyledRow>
          <Typography variant="body-14-medium" color={COLOR.darkGray}>
            {t('selectModel')}
          </Typography>
          <div>
            <StyledModelAnchor
              role="button"
              tabIndex={0}
              onClick={(e) => setModelAnchorEl(e.currentTarget)}
            >
              <Typography variant="body-14-regular" color={COLOR.darkGray}>
                {selectedModel}
              </Typography>
              <ArrowDropDownRoundedIcon
                sx={{ width: 20, height: 20, color: COLOR.navyBlue }}
              />
            </StyledModelAnchor>
            <Popover
              open={Boolean(modelAnchorEl)}
              anchorEl={modelAnchorEl}
              onClose={() => setModelAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  width: '318px',
                  marginTop: '8px',
                  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08) !important',
                },
              }}
            >
              <StyledPopoverList>
                {transcriptAIModels.map((model) => (
                  <StyledPopoverItem
                    key={model?.model}
                    role="button"
                    tabIndex={0}
                    $active={selectedModel === model?.model}
                    onClick={() => {
                      setSelectedModel(model?.model);
                      setModelAnchorEl(null);
                    }}
                  >
                    <StyledPopoverItemText>
                      <Typography
                        variant="body-14-medium"
                        color={COLOR.darkGray}
                      >
                        {model.model}
                      </Typography>
                      <Typography
                        variant="body-12-regular"
                        color={COLOR.label2[64]}
                      >
                        {getModelDescription(model)}
                      </Typography>
                      <StyledItalicTypography
                        variant="body-12-regular"
                        color={COLOR.label2[64]}
                      >
                        {t('creditPerSecond', { value: model.creditPerSecond })}
                      </StyledItalicTypography>
                    </StyledPopoverItemText>
                    <CheckRoundedIcon
                      sx={{
                        width: 20,
                        height: 20,
                        color: COLOR.navyBlue,
                        opacity: selectedModel === model?.model ? 1 : 0,
                      }}
                    />
                  </StyledPopoverItem>
                ))}
              </StyledPopoverList>
            </Popover>
          </div>
        </StyledRow>
      )}
      <StyledRow>
        <Typography variant="body-14-medium" color={COLOR.darkGray}>
          {t('selectContentLanguage')}
        </Typography>
        <DropdownCustom
          variant="form-select-light"
          width="240px"
          options={languageOptions}
          onChange={(value) => setContentLanguage(value)}
          value={contentLanguage}
          searchable
          searchPlaceholder={t('search')}
          title={
            <Typography variant="body-14-regular" color={COLOR.darkGray}>
              {getSelectedLabel(contentLanguage) || t('selectLanguage')}
            </Typography>
          }
        />
      </StyledRow>

      <StyledTipBox>
        <TipsAndUpdatesRoundedIcon
          sx={{ width: 20, height: 20, color: COLOR.blueV2, flexShrink: 0 }}
        />
        <StyledTipText
          variant="body-14-regular"
          color={COLOR.darkGray}
          dangerouslySetInnerHTML={{ __html: t('transcriptLanguageTip') }}
        />
      </StyledTipBox>
    </StyledExtraContent>
  );

  const fileDetails = duration > 0 ? [formatDuration(duration)] : [];

  useEffect(() => {
    if (!loadingGetContentFile) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [loadingGetContentFile]);

  return (
    <>
      <PopupUpPkgUseRewrite
        open={openPopupUpPkg}
        onClose={() => setOpenPopupUpPkg(false)}
        pkgName={upgradePkgName || undefined}
        featureKey={missingFeature}
        surface={MOE_SURFACE.PROJECT_CREATE}
        product={PROJECT_PRODUCT.DUBBING}
      />
      {openDialogLockedCycle && (
        <NotificationDialogV4
          image={LockCycle}
          title={t('charactersTemporarilyLocked')}
          description={t('descriptionPopupLockedCredit')}
          actionComponent={
            <Button
              variant="contained"
              onClick={() => setOpenDialogLockedCycle(false)}
            >
              {t('understoodV3')}
            </Button>
          }
          open
          onClose={() => setOpenDialogLockedCycle(false)}
        />
      )}
      {openDialogNotEnoughCredits && (
        <ActionDialog
          open
          onClose={() => setOpenDialogNotEnoughCredits(false)}
          title={t('checkNotEnoughCreditsTitle')}
          styleTitle={{ textAlign: 'center' }}
          contentComponent={
            <Typography
              style={{ textAlign: 'center' }}
              variant="body-16-regular"
              color={COLOR.descriptionGray}
              dangerouslySetInnerHTML={{
                __html: t('checkNotEnoughCreditsDesc'),
              }}
            />
          }
          fullWidth
          image={CrownImg}
          imageContainerSize="100px"
          width="640px"
          actionComponent={
            <Button
              variant="contained"
              onClick={() => {
                setOpenDialogNotEnoughCredits(false);
                history.push(route.PAYMENT);
              }}
            >
              {t('buyNow')}
            </Button>
          }
        />
      )}
      <NavigationBlocker
        when={loadingGetContentFile}
        navigate={(path) => history.push(path)}
        description={t('leaveWhileProcessingDescriptionV2')}
        onLeave={() => {
          isLeavingRef.current = true;
        }}
      />
      <CommonDialog
        open={open}
        onClose={onClose}
        title={t('uploadVideoAudio')}
        disableAutoClose
        disableClose={loadingGetContentFile}
        width="640px"
        contentComponent={
          <FileDropzoneV3
            fileType={TRANSCRIPT_FILE_FORMAT}
            maxSize={MAX_AUDIO_SIZE_BYTES}
            maxSizeLabel={t('audioFileLimit', {
              size: formatFileSize(MAX_AUDIO_SIZE_BYTES),
              minutes: MAX_AUDIO_DURATION_SECONDS / 60,
            })}
            onAddFile={handleAddFile}
            onChangeFile={handleFileChange}
            loadingGetContentFile={loadingGetContentFile}
            extraContent={extraContent}
            extraFileDetails={fileDetails}
            submitTooltip={
              creditCost
                ? t('costsCredit', {
                    value: creditCost.toLocaleString(currentLang),
                  })
                : null
            }
            dataId="upload-audio"
          />
        }
      />
    </>
  );
};

export default UploadFileByAudio;
